import { Box, BoxProps, Stack } from '@mui/material';
import clsx from 'clsx';
import { ReactNode } from 'react';

import { TypographyProps } from '../../Typography';

import { DsFormHelperText } from '../../../components/controls/form-helper-text';
import { SxProps, WithRef } from '../../../types';
import { mergeSx } from '../../../utils';

import { LabelWithDescription } from '../LabelWithDescription';
import type { ControlLabelLabelWeight } from '../ControlLabel';

import { controlGroupClassNames } from './classNames';
import { ControlGroupSize } from './types';

export type ControlGroupSlotProps = {
  label?: Partial<TypographyProps>;
  descriptionWrapper?: Omit<Partial<TypographyProps>, 'variant'>;
  errorMessageWrapper?: Partial<BoxProps>;
};

export type ControlGroupProps = {
  /** Group label. Omit it (with description) for a headerless group of controls. */
  label?: ReactNode;
  /** Description row rendered BELOW the label (outside the control list). */
  description?: ReactNode;
  /** Content for the info tooltip rendered after the label. */
  infoTooltip?: ReactNode;
  /** Label font weight. Precedence: labelWeight -> slotProps.label.variant -> medium (default). */
  labelWeight?: ControlLabelLabelWeight;
  /** Disabled state: dims the group's label and description. Does not affect the controls inside. */
  disabled?: boolean;
  /**
   * Error state — the message to render. A truthy value marks the group invalid
   * and prints this text below the controls. One prop, so a message always
   * implies the error state.
   */
  error?: ReactNode;
  /** Size variant: drives the group label size. Does not affect the controls inside. */
  size?: ControlGroupSize;
  /** The grouped controls. */
  children?: ReactNode;
  /** Per-slot props for label, descriptionWrapper, and errorMessageWrapper. */
  slotProps?: ControlGroupSlotProps;
  /** Additional CSS class */
  className?: string;
  /** MUI sx prop */
  sx?: SxProps;
  /** `data-testid` attribute value */
  testId?: string;
};

/**
 * Gap between controls in the list. Fixed by decision — the repo's control
 * groups are all vertical and use an 8px gap regardless of size (§F.11).
 */
const CONTROLS_GAP = '8px';

/** Gap between the header block (label + description) and the controls list. */
const HEADER_GAP = '12px';

/**
 * Groups option controls under one label and description, e.g. a set of
 * checkboxes, radios or switches. Purely presentational: it owns no
 * value/selection state and sets nothing on its children — apply any `size`,
 * `disabled` or error styling to each child individually.
 */
export const ControlGroup = ({
  ref,
  label,
  description,
  infoTooltip,
  labelWeight,
  disabled,
  error,
  size = 'sm',
  children,
  slotProps,
  className,
  sx,
  testId,
}: WithRef<HTMLDivElement, ControlGroupProps>) => {
  // `error` carries the message, so a message implies the group's error state.
  // A boolean `true` is still accepted for error styling with no text of its own.
  const hasError = !!error;
  const hasErrorMessage = hasError && typeof error !== 'boolean';

  const hasHeader = !!label || !!description;
  const hasControls = !!children;

  // Group labels carry heading weight by default (medium); an explicit
  // `labelWeight` wins, then slotProps.label.variant, then the medium default
  // (a variant class keeps its own weight when no explicit labelWeight is set).
  const resolvedLabelWeight =
    labelWeight ?? (slotProps?.label?.variant ? undefined : 'medium');

  return (
    <Box
      ref={ref}
      data-testid={testId}
      className={clsx(
        controlGroupClassNames.root,
        disabled && controlGroupClassNames.disabled,
        hasError && controlGroupClassNames.error,
        className
      )}
      sx={sx}
    >
      {hasHeader && (
        <Box
          className={controlGroupClassNames.header}
          sx={{
            // The description sits directly under the label at every size.
            marginBottom: hasControls ? HEADER_GAP : 0,
          }}
        >
          <LabelWithDescription
            label={label}
            description={description}
            infoTooltip={infoTooltip}
            size={size}
            labelWeight={resolvedLabelWeight}
            disabled={disabled}
            slotProps={{
              label: slotProps?.label,
              descriptionWrapper: slotProps?.descriptionWrapper,
            }}
          />
        </Box>
      )}

      {!!children && (
        <Stack
          className={controlGroupClassNames.controls}
          sx={{
            gap: CONTROLS_GAP,
            // A nested radio group is a single child, so the list gap above
            // never reaches its options — MUI's RadioGroup is a plain flex
            // column with no gap of its own. Space its options the same way.
            '& .MuiRadioGroup-root': { gap: CONTROLS_GAP },
          }}
        >
          {children}
        </Stack>
      )}

      {/* The message is the error state — rendered whenever there is text. */}
      {hasErrorMessage && (
        <Box
          {...slotProps?.errorMessageWrapper}
          className={clsx(
            controlGroupClassNames.errorMessage,
            slotProps?.errorMessageWrapper?.className
          )}
          sx={mergeSx(
            // 8px between the controls and the message — no extra padding
            // (the header gap does not apply to this slot).
            { marginTop: CONTROLS_GAP },
            slotProps?.errorMessageWrapper?.sx
          )}
        >
          <DsFormHelperText error>{error}</DsFormHelperText>
        </Box>
      )}
    </Box>
  );
};

ControlGroup.displayName = 'ControlGroup';
